import { useCallback, useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo, Spinner, hueFor } from "../components/Icons";
import { Results } from "../components/live/Results";
import { useLiveSocket, type LiveMessage } from "../lib/live";
import type { PresenterState } from "../lib/types";

export default function Present() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const id = Number(sessionId);

  const [state, setState] = useState<PresenterState | null>(null);
  const [now, setNow] = useState(Date.now());
  const [reactions, setReactions] = useState<{ id: number; emoji: string; x: number }[]>([]);
  const [joinOpen, setJoinOpen] = useState(false);

  const onMessage = useCallback((message: LiveMessage) => {
    if (message.t === "state") setState(message as unknown as PresenterState);
    else if (message.t === "stats") {
      setState((current) => {
        // Ignore stats for a slide we have already moved past, or a late
        // one could paint the previous question's numbers under this
        // question's title.
        if (
          current &&
          message.slide_id !== undefined &&
          message.slide_id !== current.slide?.id
        ) {
          return current;
        }
        return current
          ? {
              ...current,
              stats: (message.stats as PresenterState["stats"]) ?? current.stats,
              connected: (message.connected as number) ?? current.connected,
              joined: (message.joined as number) ?? current.joined,
            }
          : current;
      });
    } else if (message.t === "reaction") {
      setReactions((current) => [
        ...current.slice(-14),
        { id: Date.now() + Math.random(), emoji: String(message.emoji), x: 8 + Math.random() * 84 },
      ]);
    }
  }, []);

  const { status, send } = useLiveSocket(Number.isFinite(id) ? `/ws/present?session_id=${id}` : null, onMessage);

  // The quiz countdown needs a repaint each tick; nothing else does.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") return setJoinOpen(false);
      // A room joining is its own moment: J blows the code up full screen
      // so nobody has to squint at a corner.
      if (event.key === "j" || event.key === "J") return setJoinOpen((v) => !v);
      if (event.key === "ArrowRight") send({ t: "next" });
      if (event.key === "ArrowLeft") send({ t: "prev" });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [send]);

  if (!state) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <Spinner size={28} />
      </div>
    );
  }

  const { session, slide, stats } = state;
  const accent = hueFor(state.slide_index);
  const joinUrl = `${location.origin}/user?code=${session.code}`;
  const isLast = state.slide_index >= session.total_slides - 1;

  const seconds = slide?.type === "quiz" ? Number(slide.config.seconds ?? 20) : 0;
  const startedAt = session.slide_started_at ? Date.parse(session.slide_started_at) : 0;
  const remaining = seconds ? Math.max(0, seconds - (now - startedAt) / 1000) : 0;
  const quizRunning = seconds > 0 && remaining > 0 && !session.revealed;

  async function finish() {
    send({ t: "end" });
    navigate(`/summary/${id}`);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        background: `radial-gradient(900px 500px at 85% -10%, ${accent}22, transparent 60%), var(--bg)`,
      }}
    >
      <header style={{ display: "flex", alignItems: "center", gap: 16, padding: 20, flexWrap: "wrap" }}>
        {/* The join code rides along on every slide. Sized to be read from
            the back of a room, not from the presenter's own laptop. */}
        <button
          onClick={() => setJoinOpen(true)}
          title="Show the join code full screen"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            background: "var(--surface)",
            border: "1px solid var(--outline)",
            borderRadius: 22,
            padding: "12px 20px 12px 14px",
            boxShadow: "0 2px 14px rgba(19,39,48,0.10)",
            cursor: "zoom-in",
            textAlign: "left",
          }}
        >
          <QRCodeCanvas value={joinUrl} size={104} bgColor="#ffffff" fgColor="#1d4a5e" level="M" />
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "var(--text-secondary)",
                marginBottom: 2,
              }}
            >
              {location.host}/user
            </div>
            <div
              style={{
                fontSize: 44,
                fontWeight: 500,
                letterSpacing: 4,
                lineHeight: 1.05,
                color: "var(--primary)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {session.code.slice(0, 3)} {session.code.slice(3)}
            </div>
          </div>
        </button>

        <div style={{ flex: 1 }} />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "var(--surface)",
            border: "0.5px solid var(--outline)",
            borderRadius: "var(--radius-pill)",
            padding: "8px 16px",
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "var(--radius-pill)",
              background: status === "open" ? "var(--success)" : "var(--warning)",
            }}
          />
          <b>{state.joined}</b>
          <span className="muted">joined</span>
          <span style={{ color: "var(--outline)" }}>|</span>
          <b>{stats?.total ?? 0}</b>
          <span className="muted">answers</span>
        </div>
        <Logo size={26} />
      </header>

      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0 32px 20px",
          minHeight: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: "var(--radius-pill)", background: accent }} />
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.4, color: accent }}>
            {state.slide_index + 1} / {session.total_slides} · {slide?.type_label}
          </span>
        </div>

        <h2 style={{ textAlign: "center", maxWidth: 880, fontSize: 32, marginBottom: 20 }}>
          {slide?.question || "Untitled question"}
        </h2>

        {quizRunning && (
          <div style={{ width: "min(520px, 80%)", marginBottom: 20 }}>
            <div style={{ height: 8, background: "var(--surface-variant)", borderRadius: 999 }}>
              <div
                style={{
                  height: "100%",
                  width: `${(remaining / seconds) * 100}%`,
                  background: accent,
                  borderRadius: 999,
                  transition: "width 250ms linear",
                }}
              />
            </div>
            <div className="tiny" style={{ textAlign: "center", marginTop: 6 }}>
              {Math.ceil(remaining)}s left
            </div>
          </div>
        )}

        <div style={{ width: "100%", maxWidth: 820, flex: 1, display: "flex", alignItems: "center" }}>
          {slide && (
            <Results
              type={slide.type}
              config={slide.config}
              stats={stats}
              accent={accent}
              revealed={session.revealed}
              reveal={state.reveal}
              joined={state.joined}
            />
          )}
        </div>
      </main>

      <footer style={{ display: "flex", gap: 10, justifyContent: "center", padding: 20, flexWrap: "wrap" }}>
        <button className="btn btn-outline" onClick={() => send({ t: "prev" })} disabled={state.slide_index === 0}>
          ← Back
        </button>
        {(slide?.type === "quiz" || slide?.type === "guess") && !session.revealed && (
          <button className="btn" onClick={() => send({ t: "reveal" })}>
            Reveal answer
          </button>
        )}
        <button className="btn btn-text" onClick={() => send({ t: "reset_slide" })}>
          Clear answers
        </button>
        {isLast ? (
          <button className="btn" onClick={() => void finish()}>
            Finish &amp; see results
          </button>
        ) : (
          <button className="btn" onClick={() => send({ t: "next" })}>
            Next →
          </button>
        )}
        <button className="btn btn-text" onClick={() => navigate("/admin")}>
          Exit
        </button>
      </footer>

      {joinOpen && (
        <div
          onClick={() => setJoinOpen(false)}
          role="dialog"
          aria-label="Join this session"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 26,
            background: "var(--surface)",
            cursor: "zoom-out",
            animation: "cardIn 180ms var(--ease-default)",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 600, color: "var(--text-secondary)" }}>
            Join at {location.host}/user
          </div>
          <div style={{ padding: 22, background: "#fff", borderRadius: 28 }}>
            <QRCodeCanvas
              value={joinUrl}
              size={Math.min(360, Math.round(window.innerHeight * 0.42))}
              bgColor="#ffffff"
              fgColor="#1d4a5e"
              level="M"
            />
          </div>
          <div
            style={{
              fontSize: "clamp(64px, 12vw, 150px)",
              fontWeight: 500,
              letterSpacing: 10,
              lineHeight: 1,
              color: "var(--primary)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {session.code.slice(0, 3)} {session.code.slice(3)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "var(--radius-pill)",
                background: "var(--success)",
                animation: "breathe 2.4s var(--ease-default) infinite",
              }}
            />
            <span className="muted" style={{ fontSize: 18 }}>
              {state.joined} joined · press Esc or click to go back
            </span>
          </div>
        </div>
      )}

      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        {reactions.map((reaction) => (
          <span
            key={reaction.id}
            style={{
              position: "absolute",
              bottom: 80,
              left: `${reaction.x}%`,
              fontSize: 32,
              animation: "floatUp 2.8s var(--ease-default) forwards",
            }}
          >
            {reaction.emoji}
          </span>
        ))}
      </div>
    </div>
  );
}
